import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    ResponsiveContainer
} from 'recharts';
import {
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

/**
 * Page des statistiques User ↔ Listings
 */
const UserListingsStatsPage = () => {
    // États des statistiques
    const [stats, setStats] = useState(null);
    const [advancedStats, setAdvancedStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [advancedLoading, setAdvancedLoading] = useState(false);
    const [error, setError] = useState(null);

    // États des filtres
    const [period, setPeriod] = useState('30d');
    const [status, setStatus] = useState('');
    const [category, setCategory] = useState('');

    // Couleurs pour les graphiques
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    /**
     * Charger les statistiques de base
     */
    const loadBasicStats = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await userListingService.getStatistics();
            // La réponse API doit maintenant contenir les données correctes
            setStats(response.data || response);
        } catch (err) {
            console.error('Erreur lors du chargement des statistiques:', err);
            setError('Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Charger les statistiques avancées
     */
    const loadAdvancedStats = async () => {
        try {
            setAdvancedLoading(true);
            
            const filters = {
                period,
                ...(status && { status }),
                ...(category && { category })
            };
            
            const response = await userListingService.getAdvancedStatistics(filters);
            // La réponse API doit maintenant contenir les données correctes
            setAdvancedStats(response.data || response);
        } catch (err) {
            console.error('Erreur lors du chargement des statistiques avancées:', err);
            setError('Erreur lors du chargement des statistiques avancées');
        } finally {
            setAdvancedLoading(false);
        }
    };

    /**
     * Charger les données au montage
     */
    useEffect(() => {
        loadBasicStats();
        loadAdvancedStats();
    }, []);

    /**
     * Recharger les statistiques avancées lors du changement de filtres
     */
    useEffect(() => {
        loadAdvancedStats();
    }, [period, status, category]);

    /**
     * Formater les nombres avec des espaces pour les milliers
     */
    const formatNumber = (num) => {
        return new Intl.NumberFormat('fr-FR').format(num);
    };

    /**
     * Cartes de statistiques
     */
    const StatCard = ({ title, value, icon, color, trend }) => (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography color="textSecondary" gutterBottom variant="overline">
                            {title}
                        </Typography>
                        <Typography variant="h4" component="h2">
                            {formatNumber(value)}
                        </Typography>
                        {trend && (
                            <Typography variant="body2" color={trend > 0 ? 'success.main' : 'error.main'}>
                                {trend > 0 ? '+' : ''}{trend}% vs période précédente
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ color: color }}>
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading && !stats) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PageHeader
                title="Statistiques User ↔ Listings"
                subtitle="Analysez les données des relations entre utilisateurs et annonces"
                icon={<TimelineIcon />}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filtres pour les statistiques avancées */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Filtres d'analyse
                </Typography>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Période</InputLabel>
                            <Select
                                value={period}
                                label="Période"
                                onChange={(e) => setPeriod(e.target.value)}
                            >
                                <MenuItem value="7d">7 derniers jours</MenuItem>
                                <MenuItem value="30d">30 derniers jours</MenuItem>
                                <MenuItem value="90d">90 derniers jours</MenuItem>
                                <MenuItem value="1y">1 an</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Statut</InputLabel>
                            <Select
                                value={status}
                                label="Statut"
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="">Tous les statuts</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="pending">En attente</MenuItem>
                                <MenuItem value="rejected">Rejetée</MenuItem>
                                <MenuItem value="suspended">Suspendue</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Catégorie</InputLabel>
                            <Select
                                value={category}
                                label="Catégorie"
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <MenuItem value="">Toutes les catégories</MenuItem>
                                <MenuItem value="services">Services</MenuItem>
                                <MenuItem value="products">Produits</MenuItem>
                                <MenuItem value="immobilier">Immobilier</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                loadBasicStats();
                                loadAdvancedStats();
                            }}
                            startIcon={<RefreshIcon />}
                            disabled={loading || advancedLoading}
                        >
                            Actualiser
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Cartes de statistiques générales */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Utilisateurs"
                            value={(stats.overview && stats.overview.totalUsers) || 
                                  (stats.data && stats.data.totalUsers) || 0}
                            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                            color="primary.main"
                            trend={(stats.usersTrend) || 0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Annonces"
                            value={(stats.overview && stats.overview.totalListings) || 
                                  (stats.data && stats.data.totalListings) || 0}
                            icon={<AssignmentIcon sx={{ fontSize: 40 }} />}
                            color="secondary.main"
                            trend={(stats.listingsTrend) || 0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Annonces Actives"
                            value={
                                (stats.listingsByStatus && stats.listingsByStatus.find(s => s.status === 'active')?.count) ||
                                (stats.data && stats.data.listingsByStatus && stats.data.listingsByStatus.active) || 0
                            }
                            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
                            color="success.main"
                            trend={(stats.activeListingsTrend) || 0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Utilisateurs Actifs"
                            value={(stats.overview && stats.overview.activeUsers) || 
                                  (stats.data && stats.data.activeUsers) || 0}
                            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                            color="info.main"
                            trend={(stats.activeUsersTrend) || 0}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Graphiques statistiques */}
            {advancedStats && (
                <Grid container spacing={3}>
                    {/* Répartition par statut */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Répartition par statut
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    {(() => {
                                        // Préparer les données avec gestion des différentes structures possibles
                                        let statusData = [];
                                        if (Array.isArray(advancedStats.statusDistribution)) {
                                            statusData = advancedStats.statusDistribution;
                                        } else if (advancedStats.data && Array.isArray(advancedStats.data.categoryDistribution)) {
                                            statusData = advancedStats.data.categoryDistribution.map(item => ({
                                                name: item.category,
                                                value: item.count
                                            }));
                                        } else if (advancedStats.data && advancedStats.data.categoryDistribution) {
                                            statusData = Object.entries(advancedStats.data.categoryDistribution).map(([key, value]) => ({
                                                name: key,
                                                value: value
                                            }));
                                        }
                                        return statusData.length > 0 ? (
                                            <Pie
                                                data={statusData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                nameKey="name"
                                            >
                                                {statusData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                        ) : null;
                                    })()}
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Évolution temporelle */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Évolution dans le temps
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={
                                    advancedStats.timeEvolution || 
                                    advancedStats.trendsData || 
                                    (advancedStats.data && advancedStats.data.trendsData) || 
                                    []
                                }>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey={advancedStats.timeEvolution ? "users" : "users"} 
                                        stroke="#8884d8" 
                                        name="Utilisateurs" 
                                        strokeWidth={2}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey={advancedStats.timeEvolution ? "listings" : "listings"} 
                                        stroke="#82ca9d" 
                                        name="Annonces" 
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Top catégories */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Top catégories
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                {(() => {
                                    // Préparer les données pour le graphique
                                    let categoryData = [];
                                    
                                    // Essayer différentes structures possibles
                                    if (Array.isArray(advancedStats.topCategories)) {
                                        categoryData = advancedStats.topCategories;
                                    } else if (advancedStats.data && Array.isArray(advancedStats.data.categoryDistribution)) {
                                        categoryData = advancedStats.data.categoryDistribution.map(item => ({
                                            name: item.category,
                                            count: item.count
                                        }));
                                    }
                                    
                                    // Si nous n'avons toujours pas de données, utiliser une structure de secours
                                    if (categoryData.length === 0) {
                                        categoryData = [
                                            { name: 'Aucune donnée', count: 0 }
                                        ];
                                    }
                                    
                                    return (
                                        <BarChart data={categoryData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="count" fill="#8884d8" />
                                        </BarChart>
                                    );
                                })()}
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Utilisateurs les plus actifs */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Utilisateurs les plus actifs
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                {(() => {
                                    // Préparer les données pour le graphique
                                    let userData = [];
                                    
                                    if (Array.isArray(advancedStats.topUsers)) {
                                        userData = advancedStats.topUsers;
                                    } else if (advancedStats.data && Array.isArray(advancedStats.data.topActiveUsers)) {
                                        userData = advancedStats.data.topActiveUsers.map(user => ({
                                            name: user.name || 'Utilisateur',
                                            listingCount: user.listingCount || 0
                                        }));
                                    } else if (advancedStats.topActiveUsers) {
                                        userData = advancedStats.topActiveUsers.map(user => ({
                                            name: user.name || 'Utilisateur',
                                            listingCount: user.listingCount || 0
                                        }));
                                    }
                                    
                                    // Si nous n'avons toujours pas de données, utiliser une structure de secours
                                    if (userData.length === 0) {
                                        userData = [
                                            { name: 'Aucune donnée', listingCount: 0 }
                                        ];
                                    }
                                    
                                    return (
                                        <BarChart data={userData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="listingCount" fill="#82ca9d" name="Nombre d'annonces" />
                                        </BarChart>
                                    );
                                })()}
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Statistiques détaillées */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Statistiques détaillées
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Moyennes
                                        </Typography>
                                        <Typography variant="body2">
                                            Annonces par utilisateur: {
                                                advancedStats.averageListingsPerUser?.toFixed(2) || 
                                                (advancedStats.data && advancedStats.data.averageListingsPerUser?.toFixed(2)) ||
                                                (advancedStats.overview && advancedStats.overview.averageListingsPerUser?.toFixed(2)) ||
                                                "0.00"
                                            }
                                        </Typography>
                                        <Typography variant="body2">
                                            Prix moyen: {
                                                formatNumber(
                                                    advancedStats.averagePrice || 
                                                    (advancedStats.data && advancedStats.data.averagePrice) || 
                                                    0
                                                )
                                            }€
                                        </Typography>
                                        <Typography variant="body2">
                                            Délai de validation: {
                                                advancedStats.averageValidationTime || 
                                                (advancedStats.data && advancedStats.data.averageValidationTime) || 
                                                "N/A"
                                            } {
                                                (advancedStats.averageValidationTime || 
                                                (advancedStats.data && advancedStats.data.averageValidationTime)) ? 
                                                "jours" : ""
                                            }
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Taux de conversion
                                        </Typography>
                                        <Typography variant="body2">
                                            Validation: {
                                                (advancedStats.validationRate?.toFixed(1)) || 
                                                (advancedStats.data && advancedStats.data.validationRate?.toFixed(1)) || 
                                                "0.0"
                                            }%
                                        </Typography>
                                        <Typography variant="body2">
                                            Rejet: {
                                                (advancedStats.rejectionRate?.toFixed(1)) || 
                                                (advancedStats.data && advancedStats.data.rejectionRate?.toFixed(1)) || 
                                                "0.0"
                                            }%
                                        </Typography>
                                        <Typography variant="body2">
                                            Activité: {
                                                (advancedStats.activityRate?.toFixed(1)) || 
                                                (advancedStats.data && advancedStats.data.activityRate?.toFixed(1)) || 
                                                "0.0"
                                            }%
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Tendances
                                        </Typography>
                                        {(() => {
                                            const growthRate = advancedStats.growthRate || 
                                                              (advancedStats.data && advancedStats.data.growthRate) || 0;
                                            return (
                                                <Typography variant="body2" color={growthRate > 0 ? 'success.main' : 'error.main'}>
                                                    Croissance: {growthRate > 0 ? '+' : ''}{growthRate?.toFixed(1) || "0.0"}%
                                                </Typography>
                                            );
                                        })()}
                                        <Typography variant="body2">
                                            Pic d'activité: {
                                                advancedStats.peakHour || 
                                                (advancedStats.data && advancedStats.data.peakHour) || 
                                                "N/A"
                                            }{
                                                (advancedStats.peakHour || 
                                                (advancedStats.data && advancedStats.data.peakHour)) ? 
                                                "h" : ""
                                            }
                                        </Typography>
                                        <Typography variant="body2">
                                            Jour le plus actif: {
                                                advancedStats.peakDay || 
                                                (advancedStats.data && advancedStats.data.peakDay) || 
                                                "N/A"
                                            }
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {advancedLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default UserListingsStatsPage;
